import { apiClient } from './client'
import type { ApiResponse } from '@/types/api'

// ============================================================================
// Types
// ============================================================================

export type PrintBookMaterialType = 'CHECKUP_BOOK' | 'WORKBOOK'
export type PrintBookScopeLevel = 'CURRICULUM' | 'COURSE' | 'GLOBAL'
export type PrintBookSourceType = 'GENERATED_PREVIEW' | 'EXTERNAL_DESIGN'

/**
 * print_book.status — 교재 발주 상태. 편집 화면에서 사람이 갱신한다.
 * 저장값은 그대로 두고 표시명만 초안/준비됨/완료 → 미발주/발주/입고로 바꿨다(2026-09-29, 기존 완료 = 입고).
 */
export type PrintBookStatus = 'draft' | 'ready' | 'completed'
export const PRINT_BOOK_STATUSES: PrintBookStatus[] = ['draft', 'ready', 'completed']
export const PRINT_BOOK_STATUS_LABELS: Record<PrintBookStatus, string> = {
  draft: '미발주',
  ready: '발주',
  completed: '입고',
}
export function printBookStatusLabel(value: string | null | undefined): string {
  return value === 'draft' || value === 'ready' || value === 'completed' ? PRINT_BOOK_STATUS_LABELS[value] : (value ?? '')
}
export function toPrintBookStatus(value: string | null | undefined): PrintBookStatus {
  return value === 'ready' || value === 'completed' ? value : 'draft'
}

export interface PrintBookListItem {
  id: number
  title: string
  subtitle: string | null
  bookLabel: string
  coverImageUrl: string | null
  learnerCoverImageUrl: string | null
  status: string
  moduleCount: number
  lessonCount: number
  totalPageCount: number | null
  confirmedLessonCount: number
  totalLessonCount: number
  materialType: PrintBookMaterialType | null
  volumeLabel: string | null
  scopeLevel: PrintBookScopeLevel | null
  scopeContentId: number | null
  sourceType: PrintBookSourceType
  noticeEnabled: boolean
  noticeMessage: string | null
  samplePdfUrl: string | null
  nextBookId: number | null
  happydayItemCode: number | null
  happydayIsbnCode: string | null
  createdAt: string
  updatedAt: string
}

export interface PrintBookDetail {
  id: number
  title: string
  subtitle: string | null
  bookLabel: string
  description: string | null
  coverImageUrl: string | null
  learnerCoverImageUrl: string | null
  status: string
  totalPageCount: number | null
  printSettings: string
  materialType: PrintBookMaterialType | null
  volumeLabel: string | null
  scopeLevel: PrintBookScopeLevel | null
  scopeContentId: number | null
  sourceType: PrintBookSourceType
  noticeEnabled: boolean
  noticeMessage: string | null
  samplePdfUrl: string | null
  nextBookId: number | null
  happydayItemCode: number | null
  happydayIsbnCode: string | null
  modules: PrintBookModule[]
  createdBy: number | null
  createdAt: string
  updatedAt: string
}

export interface PrintBookModule {
  id: number
  moduleId: number
  moduleName: string | null
  bareTitle: string | null
  sortOrder: number
  chapterNumber: number
  titleOverride: string | null
  lessons: PrintBookLesson[]
}

export interface PrintBookLesson {
  id: number
  lessonId: number
  lessonName: string | null
  bareTitle: string | null
  grade: string | null
  sortOrder: number
  pageCount: number | null
  startPage: number | null
  blockQuizStartPage: number | null
  hasMemoPage: boolean
  confirmed: boolean
  confirmedAt: string | null
  printBookLessonSettings: string | null
}

export interface PrintBookLessonQuizSettings {
  scale?: number
  /** 추가 이미지 스케일 (key = additionalImages 인덱스 문자열) */
  additionalScales?: Record<string, number>
  /** 보기 이미지 스케일 (퀴즈 내 모든 보기 이미지에 일괄 적용) */
  optionScale?: number
  spacing?: number
}

export interface PrintBookLessonStepSettings {
  quizzes: Record<string, PrintBookLessonQuizSettings>
}

export interface PrintBookLessonSettings {
  defaultScale: number
  /** 추가 이미지 기본 스케일 */
  defaultAdditionalScale?: number
  /** 보기 이미지 기본 스케일 */
  defaultOptionScale?: number
  /** Step별 기본 간격 (key = step 인덱스 문자열: 0, 1, 2) */
  defaultStepSpacings?: Record<string, number>
  /** 레슨 제목 override. 빈 문자열/미설정이면 원본 제목 사용. 줄바꿈(\n)으로 줄 분할, 최대 2줄. */
  titleOverride?: string
  steps: Record<string, PrintBookLessonStepSettings>
}

export interface PrintBookCreateRequest {
  title: string
  subtitle?: string
  bookLabel?: string
  description?: string
  coverImageUrl?: string | null
  learnerCoverImageUrl?: string | null
  printSettings?: string
  modules?: PrintBookModuleRequest[]
  materialType?: PrintBookMaterialType | null
  volumeLabel?: string | null
  scopeLevel?: PrintBookScopeLevel | null
  scopeContentId?: number | null
  sourceType?: PrintBookSourceType
  noticeEnabled?: boolean
  noticeMessage?: string | null
  samplePdfUrl?: string | null
  nextBookId?: number | null
  happydayItemCode?: number | null
  happydayIsbnCode?: string | null
}

export interface PrintBookBulkCreateRequest {
  titlePattern?: string
  subtitle?: string
  bookLabel?: string
  description?: string
  coverImageUrl?: string | null
  learnerCoverImageUrl?: string | null
  printSettings?: string
  materialType?: PrintBookMaterialType | null
  scopeLevel?: PrintBookScopeLevel | null
  scopeContentId?: number | null
  sourceType?: PrintBookSourceType
  noticeMessage?: string | null
  samplePdfUrl?: string | null
  books: PrintBookBulkBookRequest[]
}

export interface PrintBookBulkBookRequest {
  orderIndex: number
  title?: string | null
  volumeLabel: string
  moduleIds: number[]
}

export interface PrintBookBulkCreateResponse {
  createdBooks: PrintBookDetail[]
  chain: {
    bookId: number
    title: string
    volumeLabel: string | null
    nextBookId: number | null
  }[]
}

export interface PrintBookUpdateRequest {
  title?: string
  subtitle?: string
  bookLabel?: string
  description?: string
  coverImageUrl?: string | null
  learnerCoverImageUrl?: string | null
  status?: string
  printSettings?: string
  modules?: PrintBookModuleRequest[]
  materialType?: PrintBookMaterialType | null
  volumeLabel?: string | null
  scopeLevel?: PrintBookScopeLevel | null
  scopeContentId?: number | null
  sourceType?: PrintBookSourceType
  noticeEnabled?: boolean
  noticeMessage?: string | null
  samplePdfUrl?: string | null
  nextBookId?: number | null
  happydayItemCode?: number | null
  happydayIsbnCode?: string | null
}

export interface PrintBookModuleRequest {
  moduleId: number
  sortOrder: number
  chapterNumber: number
  titleOverride: string | null
  lessons: PrintBookLessonRequest[]
}

export interface PrintBookLessonRequest {
  lessonId: number
  sortOrder: number
}

export interface PrintBookPagesUpdateRequest {
  totalPageCount: number
  lessons: {
    lessonId: number
    pageCount: number
    startPage: number
    hasMemoPage: boolean
    blockQuizStartPage?: number | null
    updateBlockQuizStartPage?: boolean
  }[]
}

export interface PrintBookBlockQuizPagesUpdateRequest {
  lessons: {
    lessonId: number
    blockQuizStartPage: number | null
  }[]
}

// ============================================================================
// API
// ============================================================================

export const printBookApi = {
  getAll: async (): Promise<PrintBookListItem[]> => {
    const { data } = await apiClient.get<ApiResponse<PrintBookListItem[]>>('/admin/print-books')
    return data.data!
  },

  getById: async (id: number): Promise<PrintBookDetail> => {
    const { data } = await apiClient.get<ApiResponse<PrintBookDetail>>(`/admin/print-books/${id}`)
    return data.data!
  },

  create: async (request: PrintBookCreateRequest): Promise<PrintBookDetail> => {
    const { data } = await apiClient.post<ApiResponse<PrintBookDetail>>('/admin/print-books', request)
    return data.data!
  },

  bulkCreate: async (request: PrintBookBulkCreateRequest): Promise<PrintBookBulkCreateResponse> => {
    const { data } = await apiClient.post<ApiResponse<PrintBookBulkCreateResponse>>('/admin/print-books/bulk', request)
    return data.data!
  },

  update: async (id: number, request: PrintBookUpdateRequest): Promise<PrintBookDetail> => {
    const { data } = await apiClient.put<ApiResponse<PrintBookDetail>>(`/admin/print-books/${id}`, request)
    return data.data!
  },

  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/admin/print-books/${id}`)
  },

  updatePages: async (id: number, request: PrintBookPagesUpdateRequest): Promise<PrintBookDetail> => {
    const { data } = await apiClient.put<ApiResponse<PrintBookDetail>>(`/admin/print-books/${id}/pages`, request)
    return data.data!
  },

  updateBlockQuizPages: async (
    id: number,
    request: PrintBookBlockQuizPagesUpdateRequest,
  ): Promise<PrintBookDetail> => {
    const { data } = await apiClient.put<ApiResponse<PrintBookDetail>>(
      `/admin/print-books/${id}/block-quiz-pages`,
      request,
    )
    return data.data!
  },

  confirmLesson: async (bookId: number, lessonId: number, confirmed: boolean): Promise<PrintBookDetail> => {
    const { data } = await apiClient.put<ApiResponse<PrintBookDetail>>(
      `/admin/print-books/${bookId}/lessons/${lessonId}/confirm?confirmed=${confirmed}`
    )
    return data.data!
  },

  updateLessonSettings: async (
    bookId: number,
    lessonId: number,
    settings: PrintBookLessonSettings | null
  ): Promise<void> => {
    await apiClient.put(`/admin/print-books/${bookId}/lessons/${lessonId}/settings`, {
      settings: settings ? JSON.stringify(settings) : null,
    })
  },
}
